import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule, LucideIconData,
  Compass, Unplug, Wrench, Home, FileText, LifeBuoy, Activity, Hash, Clock,
} from 'lucide-angular';

type ErrorVariant = '404' | '500' | 'maint' | 'sin-instancia' | 'suspendida';

interface ErrorConfig {
  eyebrow: string;
  glyph: LucideIconData;
  title: string;
  sub: string;
  rings: boolean;              // maint: anillos animados
  shardsFull: boolean;         // 404/500: 4 shards · maint: 2
}

/**
 * Páginas de error/sistémicas del DS v16 (F7): 404, 500 y mantención.
 * Un solo componente, la variante llega por route data `{ variant }`.
 * Full-screen (fuera del shell de gestión), token-first, dark-mode por tokens.
 * Mockup: docs/new-design-soft-rent/softrent-error-pages.html. #619
 */
@Component({
  selector: 'sr-error-page',
  standalone: true,
  imports: [LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="err">
      <div class="err__grid"></div>
      <div class="err__glow"></div>

      <div class="err__body">
        <div class="err__eyebrow">{{ cfg().eyebrow }}</div>

        <div class="err__glyph">
          <div class="err__shard s1"></div>
          <div class="err__shard s2"></div>
          @if (cfg().shardsFull) {
            <div class="err__shard s3"></div>
            <div class="err__shard s4"></div>
          }
          <div class="err__medallion">
            @if (cfg().rings) {
              <div class="ring a"></div>
              <div class="ring b"></div>
            }
            <lucide-angular [img]="cfg().glyph" [size]="52" class="err__medallion-ic"></lucide-angular>
          </div>
        </div>

        <h1 class="err__title">{{ cfg().title }}</h1>
        <p class="err__sub">{{ cfg().sub }}</p>

        @switch (variant()) {
          @case ('404') {
            <div class="err__data"><span class="k">Ruta</span><span>{{ rutaSolicitada() }}</span></div>
          }
          @case ('500') {
            @if (errorId()) {
              <div class="err__data">
                <lucide-angular [img]="icons.Hash" [size]="13" class="err__data-ic"></lucide-angular>
                <span class="k">ID</span><span>{{ errorId() }}</span>
              </div>
            }
          }
          @case ('maint') {
            <div class="err__data eta">
              <lucide-angular [img]="icons.Clock" [size]="13" class="err__data-ic"></lucide-angular>
              <span>{{ eta() }}</span>
            </div>
          }
          @case ('sin-instancia') {
            <div class="err__data"><span class="k">Dirección</span><span>{{ dominioActual() }}</span></div>
          }
          @case ('suspendida') {
            <div class="err__data"><span class="k">Dirección</span><span>{{ dominioActual() }}</span></div>
          }
        }

        <div class="err__cta">
          @switch (variant()) {
            @case ('404') {
              <button type="button" class="err__btn err__btn--primary" (click)="ir('/')">
                <lucide-angular [img]="icons.Home" [size]="15" class="err__btn-ic"></lucide-angular>Volver al inicio
              </button>
              <button type="button" class="err__btn err__btn--ghost" (click)="ir('/contratos')">
                <lucide-angular [img]="icons.FileText" [size]="15" class="err__btn-ic"></lucide-angular>Ver mis contratos
              </button>
            }
            @case ('500') {
              <button type="button" class="err__btn err__btn--primary" (click)="ir('/')">
                <lucide-angular [img]="icons.Home" [size]="15" class="err__btn-ic"></lucide-angular>Volver al inicio
              </button>
              <button type="button" class="err__btn err__btn--ghost" (click)="reportarSoporte()">
                <lucide-angular [img]="icons.LifeBuoy" [size]="15" class="err__btn-ic"></lucide-angular>Reportar a soporte
              </button>
            }
            @case ('maint') {
              <button type="button" class="err__btn err__btn--primary" (click)="ir('/')">
                <lucide-angular [img]="icons.Activity" [size]="15" class="err__btn-ic"></lucide-angular>Reintentar
              </button>
            }
            @case ('sin-instancia') {
              <a class="err__btn err__btn--primary" href="https://softrent.cl">
                <lucide-angular [img]="icons.Home" [size]="15" class="err__btn-ic"></lucide-angular>Ir a softrent.cl
              </a>
              <a class="err__btn err__btn--ghost" [href]="soporteMailto()">
                <lucide-angular [img]="icons.LifeBuoy" [size]="15" class="err__btn-ic"></lucide-angular>Escribir a soporte
              </a>
            }
            @case ('suspendida') {
              <a class="err__btn err__btn--primary" [href]="soporteMailto()">
                <lucide-angular [img]="icons.LifeBuoy" [size]="15" class="err__btn-ic"></lucide-angular>Contactar a SoftRent
              </a>
            }
          }
        </div>
      </div>

      <div class="err__foot">
        <a [href]="soporteMailto()">contacto&#64;softrent.cl</a>
        <span class="sep">·</span>
        <span class="pb">Powered by <b>SoftRent</b></span>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .err {
      position: relative; min-height: 100vh; min-height: 100dvh;
      display: flex; flex-direction: column;
      background: var(--surface-ground); color: var(--ink);
      font-family: var(--font-body, 'Geist', sans-serif); overflow: hidden;
    }
    .err__grid {
      position: absolute; inset: 0; pointer-events: none; opacity: .5;
      background-image:
        linear-gradient(var(--hairline-soft) 1px, transparent 1px),
        linear-gradient(90deg, var(--hairline-soft) 1px, transparent 1px);
      background-size: 44px 44px;
      -webkit-mask-image: radial-gradient(ellipse 70% 60% at 50% 42%, var(--ink) 30%, transparent 78%);
      mask-image: radial-gradient(ellipse 70% 60% at 50% 42%, var(--ink) 30%, transparent 78%);
    }
    .err__glow {
      position: absolute; inset: 0; pointer-events: none;
      background: radial-gradient(ellipse 60% 45% at 50% 34%, var(--accent-tint-2, rgba(229,84,8,.1)), transparent 70%);
    }
    .err__body {
      position: relative; z-index: 1; flex: 1;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      text-align: center; padding: 40px 24px;
    }
    .err__eyebrow {
      font-family: var(--font-mono, 'Geist Mono', monospace); font-size: 12px; font-weight: 600;
      text-transform: uppercase; letter-spacing: 2px; color: var(--accent);
      margin-bottom: 28px; display: inline-flex; align-items: center; gap: 7px;
    }
    .err__eyebrow::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--accent); }

    .err__glyph { position: relative; width: clamp(160px, 40vw, 220px); height: clamp(140px, 34vw, 180px); display: grid; place-items: center; }
    .err__medallion {
      position: relative; width: clamp(104px, 26vw, 148px); height: clamp(104px, 26vw, 148px);
      border-radius: 30%; display: grid; place-items: center; z-index: 2;
      background: radial-gradient(circle at 34% 28%, var(--accent-tint-2, rgba(229,84,8,.1)), var(--accent-tint, rgba(229,84,8,.06)));
      border: 1.5px solid var(--accent-tint-2, rgba(229,84,8,.1));
      box-shadow: 0 22px 48px -22px color-mix(in srgb, var(--accent) 40%, transparent);
    }
    .err__medallion-ic { color: var(--accent); }
    .err__shard { position: absolute; border-radius: 5px; background: var(--accent); z-index: 1; }
    .err__shard.s1 { width: 14%; height: 14%; top: 6%;  left: 12%; opacity: .16; transform: rotate(18deg); }
    .err__shard.s2 { width: 9%;  height: 9%;  top: 20%; right: 8%; opacity: .28; border-radius: 50%; }
    .err__shard.s3 { width: 20%; height: 20%; bottom: 4%; right: 16%; opacity: .10; transform: rotate(-14deg); }
    .err__shard.s4 { width: 7%;  height: 7%;  bottom: 22%; left: 6%; opacity: .22; border-radius: 50%; }
    .ring { position: absolute; border-radius: 50%; border: 2.5px solid transparent; inset: -18%; }
    .ring.a { border-color: var(--accent-tint-2, rgba(229,84,8,.1)); }
    .ring.b { border-top-color: var(--accent); border-right-color: var(--accent); animation: err-spin 4.5s linear infinite; }
    @keyframes err-spin { to { transform: rotate(360deg); } }
    @media (prefers-reduced-motion: reduce) { .ring.b { animation: none; } }

    .err__title {
      font-family: var(--font-hero, 'Plus Jakarta Sans', sans-serif); font-weight: 700;
      font-size: clamp(22px, 5vw, 30px); letter-spacing: -.6px; color: var(--ink);
      margin-top: 26px; line-height: 1.12;
    }
    .err__sub { font-size: clamp(14px, 2.4vw, 16px); line-height: 1.55; color: var(--ink-mute); margin-top: 14px; max-width: 42ch; }

    .err__data {
      margin-top: 26px; display: inline-flex; align-items: center; gap: 8px;
      font-family: var(--font-mono, 'Geist Mono', monospace); font-size: 13px; color: var(--ink-2);
      background: var(--surface-card); border: 1px solid var(--hairline);
      border-radius: var(--radius-pill, 999px); padding: 6px 14px;
    }
    .err__data .k { color: var(--ink-faint); }
    .err__data-ic { color: var(--ink-faint); }
    .err__data.eta {
      color: var(--accent); border-color: var(--accent-tint-2, rgba(229,84,8,.1));
      background: var(--accent-tint, rgba(229,84,8,.06)); font-family: var(--font-body, 'Geist', sans-serif); font-weight: 600;
    }
    .err__data.eta .err__data-ic { color: var(--accent); }

    .err__cta { margin-top: 30px; display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
    .err__btn {
      display: inline-flex; align-items: center; gap: 7px;
      font-family: var(--font-body, 'Geist', sans-serif); font-weight: 600; font-size: 14px;
      border-radius: var(--radius-md, 8px); padding: 11px 18px; cursor: pointer;
      border: 1px solid transparent; white-space: nowrap; text-decoration: none;
      transition: background 160ms, border-color 160ms;
    }
    .err__btn--primary { background: var(--accent); color: var(--on-accent); }
    .err__btn--primary:hover { background: var(--accent-hover); }
    .err__btn--ghost { background: var(--surface-card); color: var(--ink-2); border-color: var(--hairline-strong); }
    .err__btn--ghost:hover { background: var(--surface-hover); border-color: var(--ink-faint); }

    .err__foot {
      position: relative; z-index: 1; border-top: 1px solid var(--hairline);
      padding: 18px 24px; display: flex; align-items: center; justify-content: center;
      flex-wrap: wrap; gap: 6px 16px;
    }
    .err__foot a { font-size: 12px; color: var(--ink-mute); text-decoration: none; }
    .err__foot a:hover { color: var(--accent); }
    .err__foot .sep { color: var(--ink-ghost); }
    .err__foot .pb { font-family: var(--font-mono, 'Geist Mono', monospace); font-size: 11px; letter-spacing: .5px; text-transform: uppercase; color: var(--ink-faint); }
    .err__foot .pb b { color: var(--accent); font-weight: 600; }
  `],
})
export class SrErrorPageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  private queryParams = toSignal(this.route.queryParamMap, { requireSync: true });

  readonly icons = { Hash, Clock, Home, FileText, LifeBuoy, Activity };

  readonly variant = computed<ErrorVariant>(() => {
    const v = this.route.snapshot.data['variant'];
    return v === '500' || v === 'maint' || v === 'sin-instancia' || v === 'suspendida'
      ? v
      : '404';
  });

  /** Ruta solicitada que no existe (para el 404). */
  readonly rutaSolicitada = computed(() => this.router.url.split('?')[0] || '/');

  /** Correlation ID del 500, si vino por query (?id=). */
  readonly errorId = computed(() => this.queryParams().get('id'));

  /** Dominio desde el que se entró; es el dato útil cuando no hay instancia. */
  readonly dominioActual = computed(() => window.location.hostname);

  /** ETA de mantención (?eta=) o mensaje por defecto. */
  readonly eta = computed(() => this.queryParams().get('eta') || 'Volvemos pronto — reintenta en unos minutos');

  readonly cfg = computed<ErrorConfig>(() => {
    switch (this.variant()) {
      case '500':
        return {
          eyebrow: 'Error 500', glyph: Unplug, rings: false, shardsFull: true,
          title: 'Algo salió mal de nuestro lado',
          sub: 'Tuvimos un problema procesando tu solicitud. Ya estamos al tanto. Si necesitas seguimiento, comparte este código con soporte.',
        };
      case 'maint':
        return {
          eyebrow: 'Mantención programada', glyph: Wrench, rings: true, shardsFull: false,
          title: 'Volvemos en un rato',
          sub: 'Estamos actualizando el sistema para mejorarte la experiencia. No necesitas hacer nada — te esperamos aquí.',
        };
      // El dominio comodín hace que CUALQUIER subdominio cargue la aplicación,
      // incluso uno que no corresponde a ningún cliente. Sin esta pantalla, una
      // dirección mal escrita muestra una app rota en vez de decir qué pasa.
      case 'sin-instancia':
        return {
          eyebrow: 'Instancia no encontrada', glyph: Compass, rings: false, shardsFull: true,
          title: 'No hay ninguna cuenta en esta dirección',
          sub: 'Revisa que la dirección esté bien escrita. Si te la entregó tu empresa y aun así no funciona, escríbenos y lo vemos.',
        };
      case 'suspendida':
        return {
          eyebrow: 'Cuenta suspendida', glyph: Unplug, rings: false, shardsFull: true,
          title: 'Esta cuenta está temporalmente suspendida',
          sub: 'El acceso está en pausa y tus datos siguen intactos. Contáctanos para reactivarla.',
        };
      default:
        return {
          eyebrow: 'Error 404', glyph: Compass, rings: false, shardsFull: true,
          title: 'Esta página se nos perdió',
          sub: 'El enlace que seguiste no existe o el documento fue movido. Revisa la dirección o vuelve a un lugar conocido.',
        };
    }
  });

  soporteMailto(): string {
    const id = this.errorId();
    const asunto = id ? `Soporte SoftRent (ref ${id})` : 'Soporte SoftRent';
    return `mailto:contacto@softrent.cl?subject=${encodeURIComponent(asunto)}`;
  }

  /** Navega por router (los CTA son <button> para evitar el color global de <a>). */
  ir(path: string): void {
    this.router.navigateByUrl(path);
  }

  reportarSoporte(): void {
    window.location.href = this.soporteMailto();
  }
}
